import React, { useState } from 'react';
import { InlineSpinner } from '../common/loading';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
alpha,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  CloudQueue as CloudIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Work as WorkIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { SolutionStoryResponse, customerRequestApi } from '../../services/api';
import { FileViewer } from '../modules/FileViewer';

interface SolutionStoryModalProps {
  open: boolean;
  onClose: () => void;
  storyId: string | null;
  loading?: boolean;
}


// Status chip component
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getChipColor = () => {
    const statusLower = status.toLowerCase();
    
    if (statusLower === 'created') {
      return { bg: alpha(SEARCE_COLORS.success, 0.15), color: SEARCE_COLORS.success };
    }
    if (statusLower === 'failed') {
      return { bg: alpha(SEARCE_COLORS.error, 0.15), color: SEARCE_COLORS.error };
    }
    
    return { bg: alpha(SEARCE_COLORS.primary, 0.1), color: SEARCE_COLORS.primary };
  };

  const { bg, color } = getChipColor();

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: bg,
        color: color,
        fontWeight: 500,
        fontSize: '0.75rem',
        height: 24,
        borderRadius: '12px',
        '& .MuiChip-label': { px: 1.5 },
      }}
    />
  );
};

// Compact info display - for grid layouts
const CompactInfo: React.FC<{ icon: React.ReactNode; label: string; value: string | null | undefined; color?: string }> = ({ 
  icon, 
  label, 
  value, 
  color 
}) => {
  const neutralColor = SEARCE_COLORS.gray_400 || '#9CA3AF';
  const bgColor = color || neutralColor;
  const iconColor = color || SEARCE_COLORS.primary;
  
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        backgroundColor: alpha(bgColor, 0.05),
        border: `1px solid ${alpha(bgColor, 0.1)}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(bgColor, 0.08),
          borderColor: alpha(bgColor, 0.2),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ color: iconColor, fontSize: '1rem' }}>{icon}</Box>
        <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ 
        color: value ? SEARCE_COLORS.textPrimary : alpha(SEARCE_COLORS.textSecondary, 0.5), 
        fontSize: '0.875rem', 
        fontWeight: value ? 500 : 400,
        fontStyle: value ? 'normal' : 'italic',
        wordBreak: 'break-word', 
        pl: 2.5 
      }}>
        {value || '-'}
      </Typography>
    </Box>
  );
};

// Section component with modern card styling
const Section: React.FC<{ 
  title: string; 
  children: React.ReactNode; 
  variant?: 'default' | 'elevated' | 'subtle';
  icon?: React.ReactNode;
}> = ({ title, children, variant = 'default', icon }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return {
          backgroundColor: '#ffffff',
          border: `1px solid ${SEARCE_COLORS.border}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        };
      case 'subtle':
        return {
          backgroundColor: alpha(SEARCE_COLORS.primary, 0.02),
          border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        };
      default:
        return {
          backgroundColor: '#ffffff',
          border: `1px solid ${SEARCE_COLORS.border}`,
        };
    }
  };
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2.5,
        ...getVariantStyles(),
        transition: 'all 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pb: 2, borderBottom: `2px solid ${alpha(SEARCE_COLORS.primary, 0.15)}` }}>
        {icon && (
          <Box sx={{ 
            p: 0.75, 
            borderRadius: 1.5, 
            backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
            color: SEARCE_COLORS.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </Box>
        )}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 700, 
            color: SEARCE_COLORS.textPrimary, 
            fontSize: '1rem',
            letterSpacing: '-0.2px',
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );
};

// Text field display component
const TextFieldDisplay: React.FC<{ label: string; value: string | null | undefined; multiline?: boolean }> = ({ label, value, multiline = false }) => {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, mb: 1, fontSize: '0.875rem' }}>
        {label}
      </Typography>
      <Box
        sx={{
          p: 2,
          backgroundColor: 'transparent',
          borderRadius: 1,
          border: 'none',
          minHeight: multiline ? '100px' : 'auto',
          fontSize: '0.875rem',
          lineHeight: '1.6',
          color: value ? SEARCE_COLORS.textPrimary : alpha(SEARCE_COLORS.textSecondary, 0.5),
          fontStyle: value ? 'normal' : 'italic',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          wordBreak: 'break-word',
        }}
      >
        {value || '-'}
      </Box>
    </Box>
  );
};

// Array field display component
const ArrayFieldDisplay: React.FC<{ label: string; values: string[] | null | undefined }> = ({ label, values }) => {
  if (!values || values.length === 0) return null;
  
  const filteredValues = values.filter(v => v && v.trim());
  if (filteredValues.length === 0) return null;
  
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, mb: 1, fontSize: '0.875rem' }}>
        {label}
      </Typography>
      <List sx={{ pl: 0 }}>
        {filteredValues.map((value, index) => (
          <ListItem key={index} sx={{ pl: 0, py: 0.5 }}>
            <ListItemText
              primary={value}
              primaryTypographyProps={{
                sx: {
                  fontSize: '0.875rem',
                  color: SEARCE_COLORS.textPrimary,
                  lineHeight: 1.6,
                  wordBreak: 'break-word',
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export const SolutionStoryModal: React.FC<SolutionStoryModalProps> = ({
  open,
  onClose,
  storyId,
  loading: externalLoading = false,
}) => {
  const [story, setStory] = useState<SolutionStoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<{ path: string; name: string; type?: string; displayName?: string } | null>(null);

  // Fetch story data when modal opens and storyId is provided
  React.useEffect(() => {
    if (open && storyId) {
      setLoading(true);
      setError(null);
      customerRequestApi.getSolutionStoryById(storyId)
        .then((data) => {
          setStory(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to fetch solution story:', err);
          setError(err.response?.data?.detail || 'Failed to load solution story');
          setLoading(false);
        });
    } else if (!open) {
      // Reset state when modal closes
      setStory(null);
      setError(null);
    }
  }, [open, storyId]);

  if (!storyId) return null;

  // Handle file view
  const handleFileView = (filePath: string, fileName: string, fileType?: string, displayName?: string) => {
    setViewingFile({ path: filePath, name: fileName, type: fileType, displayName });
    setViewerOpen(true);
  };

  // Extract file path from GCS URL
  const extractFilePath = (url: string): string => {
    if (url.startsWith('gs://')) {
      const parts = url.replace('gs://', '').split('/');
      return parts.slice(1).join('/'); // Remove bucket name
    }
    if (url.includes('storage.googleapis.com')) {
      const match = url.match(/\/o\/(.+?)(?:\?|$)/);
      return match ? decodeURIComponent(match[1]) : url;
    }
    return url;
  };

  // Get file URLs from story (only when story is loaded)
  const fileUrls: string[] = story ? ((story as any).file_urls || []) : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          maxHeight: '90vh',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `2px solid ${SEARCE_COLORS.primary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
          {story ? `Solution Story for ${story.project_name || story.project_code || 'Unknown Project'}` : 'Solution Story'}
        </Typography>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 0.5, color: SEARCE_COLORS.textSecondary }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {(loading || externalLoading) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <InlineSpinner size={40} centered centerMinHeight={160} />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : !story ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography color="textSecondary">No solution story data available</Typography>
          </Box>
        ) : (
          <Box>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary, mr: 2 }}>
                  {story.request_number || story.id}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <StatusChip status={story.status} />
                </Box>
              </Box>

              {/* Submitter Info */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2.5,
                  background: `linear-gradient(135deg, ${alpha(SEARCE_COLORS.primary, 0.05)} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
                  border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.15)}`,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <CompactInfo
                      icon={<PersonIcon sx={{ fontSize: 18 }} />}
                      label="Submitter"
                      value={story.submitter_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CompactInfo
                      icon={<EmailIcon sx={{ fontSize: 18 }} />}
                      label="Email"
                      value={story.submitter_email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CompactInfo
                      icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                      label="Business Unit"
                      value={story.business_unit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CompactInfo
                      icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                      label="Sub Business Unit"
                      value={(story as any).sub_bu || null}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            {/* Main Content - Full Width Layout */}
            <Grid container spacing={3}>
              {/* Main Details - Full Width */}
              <Grid item xs={12}>
                {/* Project Context */}
                <Section 
                  title="Project Context" 
                  variant="elevated"
                  icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                        label="Project Code"
                        value={story.project_code}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                        label="Project Name"
                        value={story.project_name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                        label="Account Name"
                        value={story.account_name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<PersonIcon sx={{ fontSize: 18 }} />}
                        label="Project Owner"
                        value={story.project_owner}
                      />
                    </Grid>
                    {story.project_owner_email && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<EmailIcon sx={{ fontSize: 18 }} />}
                          label="Project Owner Email"
                          value={story.project_owner_email}
                        />
                      </Grid>
                    )}
                    {story.opportunity && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                          label="Opportunity"
                          value={story.opportunity}
                        />
                      </Grid>
                    )}
                    {(story as any).workload && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<WorkIcon sx={{ fontSize: 18 }} />}
                          label="Workload"
                          value={(story as any).workload}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Section>

                {/* Solution Story Details */}
                <Section 
                  title="Solution Story Details" 
                  variant="subtle"
                  icon={<DescriptionIcon sx={{ fontSize: 18 }} />}
                >
                  <TextFieldDisplay 
                    label="Solution Provided" 
                    value={(story as any).solution_provided} 
                  />
                  <TextFieldDisplay 
                    label="Key Objectives/Challenges" 
                    value={(story as any).key_objectives_challenges} 
                    multiline 
                  />
                  <TextFieldDisplay 
                    label="Executive Summary" 
                    value={(story as any).executive_summary} 
                    multiline 
                  />
                </Section>

                {/* Business Context & Solution */}
                <Section 
                  title="Business Context & Solution" 
                  variant="elevated"
                  icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <ArrayFieldDisplay 
                        label="Business Context" 
                        values={(story as any).business_context} 
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ArrayFieldDisplay 
                        label="Solution" 
                        values={(story as any).solution} 
                      />
                    </Grid>
                  </Grid>
                  <TextFieldDisplay 
                    label="Challenges & Whether These Changed Since Inception" 
                    value={(story as any).challenges_changed_since_inception} 
                    multiline 
                  />
                  <TextFieldDisplay 
                    label="Solution Description" 
                    value={(story as any).solution_description} 
                    multiline 
                  />
                </Section>

                {/* Platform & Technology */}
                <Section 
                  title="Platform & Technology" 
                  variant="elevated"
                  icon={<CloudIcon sx={{ fontSize: 18 }} />}
                >
                  <Grid container spacing={2}>
                    {(story as any).practice && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<CategoryIcon sx={{ fontSize: 18 }} />}
                          label="Practice"
                          value={(story as any).practice}
                        />
                      </Grid>
                    )}
                    {(story as any).platform && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<CloudIcon sx={{ fontSize: 18 }} />}
                          label="Platform"
                          value={(story as any).platform}
                        />
                      </Grid>
                    )}
                    {(story as any).platform_usage_way && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<CloudIcon sx={{ fontSize: 18 }} />}
                          label="Platform Usage"
                          value={(story as any).platform_usage_way}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <ArrayFieldDisplay 
                    label="Tech Stack" 
                    values={(story as any).tech_stack} 
                  />
                  {(story as any).tech_usage_way && (
                    <TextFieldDisplay 
                      label="Technology Usage" 
                      value={(story as any).tech_usage_way} 
                    />
                  )}
                </Section>

                {/* Impact */}
                <Section 
                  title="Impact" 
                  variant="subtle"
                  icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                >
                  <ArrayFieldDisplay 
                    label="Impact" 
                    values={(story as any).impact} 
                  />
                  <TextFieldDisplay 
                    label="Detailed Solution Impact" 
                    value={(story as any).detailed_solution_impact} 
                    multiline 
                  />
                </Section>

                {/* Classification */}
                <Section 
                  title="Classification" 
                  variant="elevated"
                  icon={<CategoryIcon sx={{ fontSize: 18 }} />}
                >
                  <Grid container spacing={2}>
                    {(story as any).referencable && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                          label="Referencable"
                          value={(story as any).referencable}
                        />
                      </Grid>
                    )}
                    {(story as any).industry_group && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<CategoryIcon sx={{ fontSize: 18 }} />}
                          label="Industry Group"
                          value={(story as any).industry_group}
                        />
                      </Grid>
                    )}
                    {(story as any).industry && (
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<CategoryIcon sx={{ fontSize: 18 }} />}
                          label="Industry"
                          value={(story as any).industry}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Section>

                {/* Optional Fields */}
                {((story as any).teamwork_collaboration || (story as any).results_business_impact) && (
                  <Section 
                    title="Additional Information" 
                    variant="subtle"
                    icon={<DescriptionIcon sx={{ fontSize: 18 }} />}
                  >
                    {(story as any).teamwork_collaboration && (
                      <TextFieldDisplay 
                        label="Teamwork & Collaboration" 
                        value={(story as any).teamwork_collaboration} 
                        multiline 
                      />
                    )}
                    {(story as any).results_business_impact && (
                      <TextFieldDisplay 
                        label="Results & Business Impact" 
                        value={(story as any).results_business_impact} 
                        multiline 
                      />
                    )}
                  </Section>
                )}

                {/* Files Section */}
                {fileUrls && fileUrls.length > 0 && (
                  <Section 
                    title="Attached Files" 
                    variant="elevated"
                    icon={<AttachFileIcon sx={{ fontSize: 18 }} />}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {fileUrls.map((url, index) => {
                        const fileName = url.split('/').pop() || `file-${index + 1}`;
                        const fileType = fileName.split('.').pop()?.toLowerCase();
                        return (
                          <Button
                            key={index}
                            fullWidth
                            variant="contained"
                            size="medium"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              const filePath = extractFilePath(url);
                              handleFileView(url, fileName, fileType, fileName);
                            }}
                            sx={{
                              backgroundColor: SEARCE_COLORS.primary,
                              color: '#ffffff',
                              textTransform: 'none',
                              fontWeight: 600,
                              py: 1.25,
                              borderRadius: 1.5,
                              boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.primary, 0.3)}`,
                              '&:hover': {
                                backgroundColor: SEARCE_COLORS.primary,
                                boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.4)}`,
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            {fileName}
                          </Button>
                        );
                      })}
                    </Box>
                  </Section>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: `1px solid ${SEARCE_COLORS.border}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: SEARCE_COLORS.primary,
            borderColor: SEARCE_COLORS.primary,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
              borderColor: SEARCE_COLORS.primary,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>

      {/* File Viewer Modal */}
      {viewingFile && (
        <FileViewer
          open={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setViewingFile(null);
          }}
          filePath={viewingFile.path}
          fileName={viewingFile.name}
          fileType={viewingFile.type}
          displayName={viewingFile.displayName}
        />
      )}
    </Dialog>
  );
};
